import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  DownloadOutlined,
  EyeOutlined,
  FilterOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Dropdown,
  Form,
  MenuProps,
  Popover,
  Row,
  Select,
  Space,
  Table,
  TableProps,
  message,
} from 'antd';
import Search from 'antd/es/input/Search';
import Column from 'antd/es/table/Column';
import { SearchProps } from 'antd/lib/input';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import {
  LeadCard,
  LeadTagMatch,
  LeadsListParams,
  LeadsResponse,
  ProfileStatusCode,
  ProfileStatusEntry,
} from 'models/Lead';

import { LeadService } from 'services/LeadService';

import { FunctionsHelper } from 'utils/FunctionsHelper';
import { toCatalogOptions } from 'utils/LeadProfileCatalogHelper';
import {
  applyTagFilterParams,
  buildAppliedTagFilter,
  clearCountryScopedDraft,
  countActiveFilters,
} from 'utils/LeadTagGridHelper';

import Layout from '../../components/Layout/Layout';
import appI18n from '../../config/i18n';
import BulkTagLeadsBar from './Components/BulkTagLeadsBar';
import BulkTagLeadsModal from './Components/BulkTagLeadsModal';
import DetailLeadModal from './Components/DetailLeadModal';
import ExportLeadsModal from './Components/ExportLeadsModal';
import LeadTagsCell from './Components/LeadTagsCell';
import LeadTagsFilter from './Components/LeadTagsFilter';
import StatusIndicator from './Components/StatusIndicator';
import {
  OTHER_UNIVERSITY_VALUE,
  getCityLabelKey,
  getRegionLabelKey,
} from './Components/leadHelpers';
import { useLeadBulkSelection } from './Components/useLeadBulkSelection';
import {
  useLeadCatalogs,
  useLeadProfileCatalogs,
} from './Components/useLeadCatalogs';

import './Leads.scss';

type SortField = 'fullName' | 'createdAt' | 'statusCode';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const PAGE_SIZE_STORAGE_KEY = 'leads.pageSize';
const DEFAULT_COUNTRY = 'CL';
const COUNTRY_OPTIONS = [{ value: 'CL' }, { value: 'PE' }, { value: 'CO' }];

const getInitialPageSize = (): number => {
  try {
    const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (PAGE_SIZE_OPTIONS.includes(parsed)) return parsed;
    }
  } catch (e) {
    // localStorage puede no estar disponible
  }
  return PAGE_SIZE;
};

const SORT_FIELD_BY_COLUMN: Record<string, SortField> = {
  lead: 'fullName',
  createdAt: 'createdAt',
  status: 'statusCode',
};

function Leads() {
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });
  const [messageApi, contextHolder] = message.useMessage();

  const [tableLoading, setTableLoading] = useState<boolean>(true);
  const [dataSource, setDataSource] = useState<LeadCard[]>([]);

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(getInitialPageSize);
  const [total, setTotal] = useState<number>(0);
  const [sortField, setSortField] = useState<SortField>('fullName');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Filtros aplicados (los que se mandan al backend)
  const [country, setCountry] = useState<string>(DEFAULT_COUNTRY);
  const [term, setTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<ProfileStatusCode[]>([]);
  // Universidades seleccionadas: ids del catálogo y/o OTHER_UNIVERSITY_VALUE
  const [universityFilter, setUniversityFilter] = useState<number[]>([]);
  // Región: selección única (string con padding significativo, p. ej. "05")
  const [regionFilter, setRegionFilter] = useState<string | undefined>(
    undefined
  );
  // Ciudades/comunas: ids integer de GET /cities
  const [cityFilter, setCityFilter] = useState<number[]>([]);
  // Etiquetas: ids del catalogo del pais; `tagMatch` solo aplica con etiquetas
  const [tagFilter, setTagFilter] = useState<number[]>([]);
  const [tagMatchFilter, setTagMatchFilter] = useState<LeadTagMatch>('any');
  // Perfil: ids de catalogo. Nacionalidad es global; genero y estado civil son
  // por pais.
  const [nationalityFilter, setNationalityFilter] = useState<number[]>([]);
  const [genderFilter, setGenderFilter] = useState<number[]>([]);
  const [maritalStatusFilter, setMaritalStatusFilter] = useState<number[]>([]);
  // Datos de pago: 'true' | 'false'; undefined = "Todos".
  const [paymentFilter, setPaymentFilter] = useState<string | undefined>(
    undefined
  );

  // Filtros en borrador (los que se editan dentro del popover)
  const [draftCountry, setDraftCountry] = useState<string>(DEFAULT_COUNTRY);
  const [draftStatus, setDraftStatus] = useState<ProfileStatusCode[]>([]);
  const [draftUniversity, setDraftUniversity] = useState<number[]>([]);
  const [draftRegion, setDraftRegion] = useState<string | undefined>(undefined);
  const [draftCities, setDraftCities] = useState<number[]>([]);
  const [draftTags, setDraftTags] = useState<number[]>([]);
  const [draftTagMatch, setDraftTagMatch] = useState<LeadTagMatch>('any');
  const [draftNationalities, setDraftNationalities] = useState<number[]>([]);
  const [draftGenders, setDraftGenders] = useState<number[]>([]);
  const [draftMaritalStatuses, setDraftMaritalStatuses] = useState<number[]>(
    []
  );
  const [draftPayment, setDraftPayment] = useState<string | undefined>(
    undefined
  );

  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);

  const [statuses, setStatuses] = useState<ProfileStatusEntry[]>([]);

  // Mientras el popover esta abierto los catalogos siguen al borrador (es lo
  // que el usuario esta editando); cerrado, siguen a los filtros aplicados.
  const catalogCountry = filtersOpen ? draftCountry : country;
  const catalogRegion = filtersOpen ? draftRegion : regionFilter;

  const {
    universities,
    universitiesLoading,
    regions,
    regionsLoading,
    cities,
    citiesLoading,
  } = useLeadCatalogs(catalogCountry, catalogRegion, true, (catalog, error) =>
    messageApi.error(
      (error.message as string) ||
        t(`scenes.leads.scene.errors.fetch_${catalog}`),
      3.5
    )
  );

  const {
    nationalities,
    nationalitiesLoading,
    genders,
    gendersLoading,
    maritalStatuses,
    maritalStatusesLoading,
  } = useLeadProfileCatalogs(catalogCountry, true, (catalog, error) =>
    messageApi.error(
      (error.message as string) ||
        t(`scenes.leads.scene.errors.fetch_${catalog}`),
      3.5
    )
  );

  const statusByCode = useMemo(() => {
    const map: Record<string, ProfileStatusEntry> = {};
    statuses.forEach((s) => {
      map[s.code] = s;
    });
    return map;
  }, [statuses]);

  const [selectedLeadId, setSelectedLeadId] = useState<string | undefined>(
    undefined
  );
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);
  // Intencion de seleccion pendiente al cruzar de pagina con las flechas del
  // modal: 'first' selecciona el primer row de la nueva pagina, 'last' el ultimo.
  // Solo se usa como fallback si el prefetch aun no termino.
  const [pendingNavSelect, setPendingNavSelect] = useState<
    'first' | 'last' | null
  >(null);
  // Prefetch de la pagina adyacente (tras bambalinas) para que el salto entre
  // paginas con las flechas sea instantaneo. Guarda { page, rows }.
  const prefetchRef = useRef<{ page: number; rows: LeadCard[] } | null>(null);
  // Cuando aplicamos un prefetch saltamos el refetch automatico del effect (la
  // data ya esta cargada y seria un fetch redundante).
  const skipNextFetchRef = useRef<boolean>(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
  const [isBulkTagOpen, setIsBulkTagOpen] = useState<boolean>(false);
  // Una sola llave para "cambio algun filtro aplicado": evita repetir la lista
  // completa en cada effect (y olvidar un filtro nuevo en alguno).
  const filtersKey = useMemo(
    () =>
      JSON.stringify([
        country,
        term,
        statusFilter,
        universityFilter,
        regionFilter,
        cityFilter,
        tagFilter,
        tagMatchFilter,
        nationalityFilter,
        genderFilter,
        maritalStatusFilter,
        paymentFilter,
      ]),
    [
      country,
      term,
      statusFilter,
      universityFilter,
      regionFilter,
      cityFilter,
      tagFilter,
      tagMatchFilter,
      nationalityFilter,
      genderFilter,
      maritalStatusFilter,
      paymentFilter,
    ]
  );

  // Seleccion masiva: por pagina; cualquier cambio de pagina, tamaño, orden,
  // busqueda o filtros la descarta. El modal usa el pais APLICADO.
  const bulk = useLeadBulkSelection({
    rows: dataSource,
    country,
    resetKey: [page, pageSize, sortField, sortDir, filtersKey].join('|'),
    refetch: () => {
      fetchData(true);
    },
  });

  // Estado "fantasma": cuando el lead seleccionado desaparece de la pagina
  // actual tras una edicion, guardamos la posicion que ocupaba para poder
  // seguir navegando por posicion en vez de perder las flechas. `outOfFilter`
  // distingue si realmente dejo de coincidir con el filtro (mostramos aviso)
  // o si solo se reordeno hacia otra pagina (sin aviso).
  const [ghost, setGhost] = useState<{
    index: number;
    outOfFilter: boolean;
  } | null>(null);
  // Captura { leadId, index, total } antes de refrescar tras una edicion, para
  // detectar si el lead sigue presente una vez llega la nueva data.
  const pendingGhostCheckRef = useRef<{
    leadId: string;
    index: number;
    total: number;
  } | null>(null);
  // Secuencia de fetches del listado: descarta respuestas que llegan fuera de
  // orden (dos refetch solapados) para no pisar data mas nueva con data vieja.
  const fetchSeqRef = useRef<number>(0);

  useEffect(() => {
    fetchStatuses();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [filtersKey]);

  // Al cambiar filtros/orden/pageSize, el prefetch cacheado queda obsoleto.
  useEffect(() => {
    prefetchRef.current = null;
  }, [filtersKey, sortField, sortDir, pageSize]);

  // El modo fantasma solo tiene sentido mientras el usuario sigue viendo la
  // misma pagina/orden/filtros donde se produjo la edicion: si cambia
  // filtros, orden, pageSize o pagina, descartamos el fantasma y cualquier
  // verificacion pendiente (para que no dispare contra data no relacionada).
  useEffect(() => {
    setGhost(null);
    pendingGhostCheckRef.current = null;
  }, [page, pageSize, sortField, sortDir, filtersKey]);

  useEffect(() => {
    // Si venimos de aplicar un prefetch, la data ya esta puesta: evitamos el
    // fetch redundante (una sola vez).
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    fetchData();
  }, [page, pageSize, sortField, sortDir, filtersKey]);

  const fetchStatuses = async () => {
    const response = await LeadService.getProfileStatuses();

    if (response.success) {
      setStatuses(response.data as ProfileStatusEntry[]);
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.leads.scene.errors.fetch_statuses'),
        3.5
      );
    }
  };

  // Construye los params del listado para una pagina dada, respetando los
  // filtros/orden aplicados. Reutilizado por fetchData y por el prefetch.
  const buildListParams = (pageArg: number): LeadsListParams => {
    const params: LeadsListParams = {
      country,
      page: pageArg,
      pageSize,
      sort: sortField,
      dir: sortDir,
    };
    if (term && term.trim().length > 0) {
      params.q = term.trim();
    }
    if (statusFilter.length > 0) {
      params.states = statusFilter;
    }
    if (universityFilter.length > 0) {
      // OTHER_UNIVERSITY_VALUE (-1) representa "Otros" → se manda null al backend
      params.universityIds = universityFilter.map((id) =>
        id === OTHER_UNIVERSITY_VALUE ? null : id
      );
    }
    if (regionFilter) {
      // El backend acepta varias regiones, pero la UI es de seleccion unica.
      params.regionIds = [regionFilter];
    }
    if (cityFilter.length > 0) {
      params.cityIds = cityFilter;
    }
    if (nationalityFilter.length > 0) {
      params.nationalityIds = nationalityFilter;
    }
    if (genderFilter.length > 0) {
      params.genderIds = genderFilter;
    }
    if (maritalStatusFilter.length > 0) {
      params.maritalStatusIds = maritalStatusFilter;
    }
    if (paymentFilter) {
      params.paymentConfigured = paymentFilter === 'true';
    }
    return applyTagFilterParams(params, tagFilter, tagMatchFilter);
  };

  // Devuelve true si la respuesta se aplico (o si quedo superada por un fetch
  // mas nuevo) y false solo ante un fallo real del request vigente.
  const fetchData = async (silent = false): Promise<boolean> => {
    const seq = ++fetchSeqRef.current;

    if (!silent) {
      setTableLoading(true);
    }

    const response = await LeadService.getLeads(buildListParams(page));

    // Guardia anti-stale: si mientras esperabamos se disparo otro fetch mas
    // nuevo, descartamos esta respuesta sin tocar estado (el fetch mas nuevo
    // se encarga de la data, del loading y de la verificacion fantasma).
    if (seq !== fetchSeqRef.current) {
      return true;
    }

    if (response.success) {
      const data = response.data as LeadsResponse;
      setDataSource(data.data);
      setTotal(data.totalRows);
    } else if (!silent) {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) || t('scenes.leads.scene.errors.fetch_leads'),
        3.5
      );
    }

    if (!silent) {
      setTableLoading(false);
    }

    return response.success;
  };

  const onSearch: SearchProps['onSearch'] = (value) => {
    setTerm(value);
  };

  const handleTableChange: TableProps<LeadCard>['onChange'] = (
    pagination,
    _filters,
    sorter
  ) => {
    if (pagination.current && pagination.current !== page) {
      setPage(pagination.current);
    }
    if (pagination.pageSize && pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
      setPage(1);
      try {
        localStorage.setItem(
          PAGE_SIZE_STORAGE_KEY,
          String(pagination.pageSize)
        );
      } catch (e) {
        // localStorage puede no estar disponible
      }
    }

    const sorterLocal = sorter as any;
    const columnKey = sorterLocal?.columnKey as string | undefined;
    const order = sorterLocal?.order as 'ascend' | 'descend' | undefined;

    if (order && columnKey && SORT_FIELD_BY_COLUMN[columnKey]) {
      setSortField(SORT_FIELD_BY_COLUMN[columnKey]);
      setSortDir(order === 'descend' ? 'desc' : 'asc');
    } else {
      setSortField('fullName');
      setSortDir('asc');
    }
  };

  const showDetailLead = (row: LeadCard) => {
    setSelectedLeadId(row.id);
    setIsDetailModalOpen(true);
    setGhost(null);
    pendingGhostCheckRef.current = null;
  };

  const handleCloseDetailModal = () => {
    setSelectedLeadId(undefined);
    setIsDetailModalOpen(false);
    setPendingNavSelect(null);
    setGhost(null);
    pendingGhostCheckRef.current = null;
    prefetchRef.current = null;
  };

  // ── Navegacion entre leads (flechas del modal) ──────────────────────────
  // Indice del lead actual dentro de la pagina cargada.
  const currentLeadIndex = useMemo(
    () => dataSource.findIndex((l) => l.id === selectedLeadId),
    [dataSource, selectedLeadId]
  );

  // Posicion global (0-based) considerando la paginacion, para saber si quedan
  // registros antes/despues aunque esten en otra pagina.
  const currentGlobalIndex =
    currentLeadIndex >= 0 ? (page - 1) * pageSize + currentLeadIndex : -1;

  // Modo fantasma: el lead seleccionado ya no esta en la pagina (dejo de
  // coincidir con el filtro o se reordeno tras una edicion) pero conservamos
  // la posicion que ocupaba para poder seguir navegando.
  const ghostIndex = ghost !== null ? ghost.index : null;
  const isGhostMode = currentLeadIndex === -1 && ghostIndex !== null;
  const ghostGlobalIndex =
    ghostIndex !== null ? (page - 1) * pageSize + ghostIndex : -1;

  const canNavigatePrev = isGhostMode
    ? ghostGlobalIndex > 0 && pendingNavSelect === null
    : currentGlobalIndex > 0 && pendingNavSelect === null;
  const canNavigateNext = isGhostMode
    ? ghostIndex !== null &&
      ghostIndex < dataSource.length &&
      pendingNavSelect === null
    : currentGlobalIndex >= 0 &&
      currentGlobalIndex < total - 1 &&
      pendingNavSelect === null;

  // Prefetch tras bambalinas: cuando el lead seleccionado es el ultimo (o el
  // primero) de la pagina y hay pagina adyacente, la cargamos en segundo plano
  // con los mismos filtros, para que la flecha en el limite salte al instante.
  useEffect(() => {
    if (!isDetailModalOpen || currentLeadIndex < 0) return;

    let targetPage: number | null = null;
    if (currentLeadIndex === dataSource.length - 1 && page * pageSize < total) {
      targetPage = page + 1;
    } else if (currentLeadIndex === 0 && page > 1) {
      targetPage = page - 1;
    }
    if (targetPage === null) return;
    // Ya esta cacheada (o cargandose) la pagina objetivo: no repetir.
    if (prefetchRef.current?.page === targetPage) return;

    let cancelled = false;
    const targetPageToFetch = targetPage;
    (async () => {
      const response = await LeadService.getLeads(
        buildListParams(targetPageToFetch)
      );
      if (cancelled || !response.success) return;
      const data = response.data as LeadsResponse;
      prefetchRef.current = { page: targetPageToFetch, rows: data.data };
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isDetailModalOpen,
    selectedLeadId,
    currentLeadIndex,
    dataSource,
    page,
    pageSize,
    total,
  ]);

  // Aplica la pagina ya prefetcheada de forma instantanea (sin loading ni
  // refetch). Devuelve true si habia cache disponible para la pagina objetivo.
  const applyPrefetchedPage = (
    targetPage: number,
    select: 'first' | 'last'
  ): boolean => {
    const cached = prefetchRef.current;
    if (!cached || cached.page !== targetPage || cached.rows.length === 0) {
      return false;
    }
    const target =
      select === 'first' ? cached.rows[0] : cached.rows[cached.rows.length - 1];
    // Invalida cualquier fetch silencioso en vuelo para que su respuesta
    // (de la pagina vieja) no pise la pagina que aplicamos desde el cache.
    fetchSeqRef.current += 1;
    skipNextFetchRef.current = true;
    setPage(targetPage);
    setDataSource(cached.rows);
    setSelectedLeadId(target.id);
    prefetchRef.current = null;
    return true;
  };

  const handleNavigatePrev = () => {
    if (pendingNavSelect !== null) return;
    if (isGhostMode) {
      // Modo fantasma: el lead ya no esta en la lista, navegamos por la
      // posicion que ocupaba (el resto de la pagina se corrio hacia arriba).
      if (ghostIndex === null) return;
      if (ghostIndex > 0) {
        setSelectedLeadId(dataSource[ghostIndex - 1].id);
        setGhost(null);
      } else if (page > 1) {
        if (!applyPrefetchedPage(page - 1, 'last')) {
          setPendingNavSelect('last');
          setPage(page - 1);
        }
        setGhost(null);
      }
      return;
    }
    if (currentLeadIndex < 0) return;
    if (currentLeadIndex > 0) {
      setSelectedLeadId(dataSource[currentLeadIndex - 1].id);
    } else if (page > 1) {
      // Primer row: cruzamos a la pagina anterior y seleccionamos el ultimo row.
      if (!applyPrefetchedPage(page - 1, 'last')) {
        // El prefetch aun no termino: fallback con loading.
        setPendingNavSelect('last');
        setPage(page - 1);
      }
    }
  };

  const handleNavigateNext = () => {
    if (pendingNavSelect !== null) return;
    if (isGhostMode) {
      // Modo fantasma: el item que antes era el "siguiente" ahora ocupa el
      // lugar que dejo libre el lead editado.
      if (ghostIndex === null) return;
      if (ghostIndex < dataSource.length) {
        setSelectedLeadId(dataSource[ghostIndex].id);
        setGhost(null);
      }
      return;
    }
    if (currentLeadIndex < 0) return;
    if (currentLeadIndex < dataSource.length - 1) {
      setSelectedLeadId(dataSource[currentLeadIndex + 1].id);
    } else if (page * pageSize < total) {
      // Ultimo row: cruzamos a la pagina siguiente y seleccionamos el primero.
      if (!applyPrefetchedPage(page + 1, 'first')) {
        // El prefetch aun no termino: fallback con loading.
        setPendingNavSelect('first');
        setPage(page + 1);
      }
    }
  };

  // Fallback: cuando el prefetch no estaba listo, la nueva pagina se carga via
  // fetchData; al llegar la data resolvemos la seleccion pendiente.
  useEffect(() => {
    if (pendingNavSelect === null) return;
    if (dataSource.length === 0) return;
    const target =
      pendingNavSelect === 'first'
        ? dataSource[0]
        : dataSource[dataSource.length - 1];
    setSelectedLeadId(target.id);
    setPendingNavSelect(null);
  }, [dataSource, pendingNavSelect]);

  // Al llegar la data refrescada tras una edicion, verificamos si el lead
  // seleccionado sigue en la pagina actual. Si desaparecio, discriminamos por
  // `total`: si el total bajo respecto al capturado antes del refetch, el lead
  // realmente dejo de coincidir con el filtro (aviso + mensajes de fuera de
  // filtro); si el total se mantuvo o crecio, solo se reordeno hacia otra
  // pagina (p. ej. orden por estado + cambio de estado) y habilitamos la
  // navegacion fantasma sin avisos. Si sigue presente, limpiamos el fantasma.
  // Limitacion conocida: en el caso reorden, si el lead se movio hacia una
  // pagina ANTERIOR, la fila que ocupa su hueco es la que era su "anterior"
  // (la pagina se rellena desde atras), por lo que prev/next pueden quedar
  // corridos en uno. Es un vecino valido y no hay señal para distinguir la
  // direccion sin consultar al backend, asi que se acepta la imprecision.
  useEffect(() => {
    const pending = pendingGhostCheckRef.current;
    if (!pending) return;
    pendingGhostCheckRef.current = null;
    const stillPresent = dataSource.some((l) => l.id === pending.leadId);
    if (stillPresent) {
      setGhost(null);
      return;
    }
    const outOfFilter = total < pending.total;
    setGhost({ index: pending.index, outOfFilter });
    if (outOfFilter) {
      messageApi.info(t('scenes.leads.scene.messages.lead_out_of_filter'), 3.5);
    }
  }, [dataSource, total, messageApi, t]);

  const handleAfterUpdate = async () => {
    // Antes de refrescar, si el modal esta abierto y el lead sigue en la
    // pagina, guardamos su posicion y el total actual para poder detectar si
    // el update lo saco del filtro o solo lo reordeno (ver effect de arriba).
    if (isDetailModalOpen && selectedLeadId && currentLeadIndex >= 0) {
      pendingGhostCheckRef.current = {
        leadId: selectedLeadId,
        index: currentLeadIndex,
        total,
      };
    }
    const ok = await fetchData(true);
    if (!ok) {
      // El refetch fallo en silencio: la verificacion pendiente nunca se va a
      // consumir contra data valida; la descartamos para que no dispare
      // contra un cambio de dataSource posterior no relacionado.
      pendingGhostCheckRef.current = null;
    }
  };

  const handleFiltersOpenChange = (open: boolean) => {
    if (open) {
      // Cuando abre el popover, sincroniza el draft con los filtros aplicados
      setDraftCountry(country);
      setDraftStatus(statusFilter);
      setDraftUniversity(universityFilter);
      setDraftRegion(regionFilter);
      setDraftCities(cityFilter);
      setDraftTags(tagFilter);
      setDraftTagMatch(tagMatchFilter);
      setDraftNationalities(nationalityFilter);
      setDraftGenders(genderFilter);
      setDraftMaritalStatuses(maritalStatusFilter);
      setDraftPayment(paymentFilter);
    }
    setFiltersOpen(open);
  };

  const handleDraftCountryChange = (value: string) => {
    setDraftCountry(value);
    // Los catalogos de universidades, regiones, ciudades y etiquetas son por
    // pais: al cambiarlo, lo seleccionado deja de existir en el nuevo catalogo.
    const cleared = clearCountryScopedDraft({
      university: draftUniversity,
      region: draftRegion,
      cities: draftCities,
      tags: draftTags,
      genders: draftGenders,
      maritalStatuses: draftMaritalStatuses,
    });
    setDraftUniversity(cleared.university);
    setDraftRegion(cleared.region);
    setDraftCities(cleared.cities);
    setDraftTags(cleared.tags);
    setDraftGenders(cleared.genders);
    setDraftMaritalStatuses(cleared.maritalStatuses);
  };

  const handleDraftRegionChange = (value?: string) => {
    setDraftRegion(value);
    // Las ciudades pertenecen a la region anterior: dejan de aplicar.
    setDraftCities([]);
  };

  const handleApplyFilters = () => {
    setCountry(draftCountry);
    setStatusFilter(draftStatus);
    setUniversityFilter(draftUniversity);
    setRegionFilter(draftRegion);
    setCityFilter(draftCities);
    // Sin etiquetas el modo no aplica: evita un refetch inutil al cambiarlo.
    const appliedTags = buildAppliedTagFilter(draftTags, draftTagMatch);
    setTagFilter(appliedTags.tagIds);
    setTagMatchFilter(appliedTags.tagMatch);
    setNationalityFilter(draftNationalities);
    setGenderFilter(draftGenders);
    setMaritalStatusFilter(draftMaritalStatuses);
    setPaymentFilter(draftPayment);
    setFiltersOpen(false);
  };

  const handleClearFilters = () => {
    setDraftCountry(DEFAULT_COUNTRY);
    setDraftStatus([]);
    setDraftUniversity([]);
    setDraftRegion(undefined);
    setDraftCities([]);
    setDraftTags([]);
    setDraftTagMatch('any');
    setDraftNationalities([]);
    setDraftGenders([]);
    setDraftMaritalStatuses([]);
    setDraftPayment(undefined);
  };

  const activeFiltersCount = countActiveFilters({
    statuses: statusFilter,
    universities: universityFilter,
    region: regionFilter,
    cities: cityFilter,
    tagIds: tagFilter,
    nationalities: nationalityFilter,
    genders: genderFilter,
    maritalStatuses: maritalStatusFilter,
    payment: paymentFilter,
  });

  const getDropdownMenu = (row: LeadCard): MenuProps['items'] => [
    {
      key: '1',
      label: (
        <span>
          <EyeOutlined style={{ marginRight: '8px' }} />
          {t('scenes.leads.scene.dropdown.view_detail')}
        </span>
      ),
      onClick: () => showDetailLead(row),
    },
  ];

  const getCountryOptions = () =>
    COUNTRY_OPTIONS.map((opt) => {
      const code = opt.value.toLowerCase();
      return {
        value: opt.value,
        label: (
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <img
              src={`https://flagcdn.com/16x12/${code}.png`}
              srcSet={`https://flagcdn.com/32x24/${code}.png 2x`}
              width={16}
              height={12}
              alt=""
              style={{ display: 'block' }}
            />
            <span>
              {t(`scenes.leads.scene.countries.${opt.value}`, opt.value)}
            </span>
          </span>
        ),
      };
    });

  // Prioriza el label del backend (ya resuelto por idioma); cae a i18n local y luego al code.
  const getStatusLabel = (
    code: string,
    backendLabel?: string | null
  ): string => {
    if (backendLabel && backendLabel.trim().length > 0) return backendLabel;
    return t(`scenes.leads.scene.status_codes.${code}`, { defaultValue: code });
  };

  const getStatusOptions = () =>
    statuses.map((s) => ({
      value: s.code,
      label: (
        <StatusIndicator
          color={s.color}
          label={getStatusLabel(
            s.code,
            i18n.language === 'en' ? s.labelEn : s.labelEs
          )}
        />
      ),
    }));

  const universityOptions = useMemo(
    () => [
      ...universities.map((u) => ({ value: u.id, label: u.name })),
      {
        value: OTHER_UNIVERSITY_VALUE,
        label: t('scenes.leads.scene.filters.university_other'),
      },
    ],
    [universities, t]
  );

  const regionOptions = useMemo(
    () => regions.map((r) => ({ value: r.regionId, label: r.regionName })),
    [regions]
  );

  // El filtro `cityIds` toma el `id` integer, NO el `cityId` varchar.
  const cityOptions = useMemo(
    () => cities.map((c) => ({ value: c.id, label: c.cityName })),
    [cities]
  );

  const nationalityOptions = useMemo(
    () => toCatalogOptions(nationalities, i18n.language),
    [nationalities, i18n.language]
  );
  const genderOptions = useMemo(
    () => toCatalogOptions(genders, i18n.language),
    [genders, i18n.language]
  );
  const maritalStatusOptions = useMemo(
    () => toCatalogOptions(maritalStatuses, i18n.language),
    [maritalStatuses, i18n.language]
  );

  // La nomenclatura de region/ciudad cambia por pais (Region/Comuna en Chile,
  // Departamento/Distrito en Peru, ...). Dentro del popover manda el pais del
  // borrador, que es el que el usuario esta editando.
  const regionLabel = t(
    `scenes.leads.scene.filters.${getRegionLabelKey(draftCountry)}`
  );
  const cityLabel = t(
    `scenes.leads.scene.filters.${getCityLabelKey(draftCountry)}`
  );

  const formatDateCell = (isoDate: string): string => {
    const datePart = FunctionsHelper.getDateFormatByLang(i18n.language);
    return dayjs(isoDate).format(`${datePart} h:mm A`);
  };

  const renderLocation = (row: LeadCard) => {
    if (!row.cityName && !row.regionName) {
      return (
        <span className="lead-cell-secondary">
          {t('scenes.leads.scene.table.empty_location')}
        </span>
      );
    }
    return (
      <div>
        <div className="lead-cell-primary">{row.cityName || '—'}</div>
        <div className="lead-cell-secondary">{row.regionName || '—'}</div>
      </div>
    );
  };

  const renderStatus = (statusCode: ProfileStatusCode, fallback: string) => {
    const entry = statusByCode[statusCode];
    const color = entry?.color || '#888';
    const label = getStatusLabel(statusCode, fallback);
    return <StatusIndicator color={color} label={label} />;
  };

  const getSortOrder = (field: SortField): 'ascend' | 'descend' | undefined => {
    if (sortField !== field) return undefined;
    return sortDir === 'asc' ? 'ascend' : 'descend';
  };

  // The fields scroll inside the panel and the footer stays visible: a panel
  // taller than the viewport would add a second page scroll.
  const filtersContent = (
    <div className="leads-filters">
      <div className="leads-filters__body">
        <Form layout="vertical" style={{ marginBottom: 0 }}>
          <Form.Item
            label={t('scenes.leads.scene.filters.country_label')}
            style={{ marginBottom: 12 }}
          >
            <Select
              value={draftCountry}
              onChange={handleDraftCountryChange}
              options={getCountryOptions()}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            label={t('scenes.leads.scene.filters.status_label')}
            style={{ marginBottom: 12 }}
          >
            <Select
              mode="multiple"
              value={draftStatus}
              onChange={(value) => setDraftStatus(value as ProfileStatusCode[])}
              options={getStatusOptions()}
              style={{ width: '100%' }}
              placeholder={t('scenes.leads.scene.filters.status_placeholder')}
              allowClear
              maxTagCount="responsive"
              disabled={statuses.length === 0}
            />
          </Form.Item>
          <Form.Item
            label={t('scenes.leads.scene.filters.university_label')}
            style={{ marginBottom: 12 }}
          >
            <Select
              mode="multiple"
              value={draftUniversity}
              onChange={(value) => setDraftUniversity(value as number[])}
              loading={universitiesLoading}
              disabled={universitiesLoading}
              options={universityOptions}
              optionFilterProp="label"
              showSearch
              style={{ width: '100%' }}
              placeholder={t(
                'scenes.leads.scene.filters.university_placeholder'
              )}
              allowClear
              maxTagCount="responsive"
            />
          </Form.Item>
          <Form.Item label={regionLabel} style={{ marginBottom: 12 }}>
            <Select
              value={draftRegion}
              onChange={(value) => handleDraftRegionChange(value as string)}
              loading={regionsLoading}
              disabled={regionsLoading}
              options={regionOptions}
              optionFilterProp="label"
              showSearch
              style={{ width: '100%' }}
              placeholder={t('scenes.leads.scene.filters.region_placeholder')}
              allowClear
            />
          </Form.Item>
          <Form.Item label={cityLabel} style={{ marginBottom: 0 }}>
            <Select
              mode="multiple"
              value={draftCities}
              onChange={(value) => setDraftCities(value as number[])}
              // Mientras se recargan las regiones el catalogo de ciudades tambien
              // queda invalidado: se muestra cargando junto con el de region.
              loading={citiesLoading || regionsLoading}
              disabled={!draftRegion || citiesLoading || regionsLoading}
              options={cityOptions}
              optionFilterProp="label"
              showSearch
              style={{ width: '100%' }}
              placeholder={
                draftRegion
                  ? t('scenes.leads.scene.filters.city_placeholder')
                  : t('scenes.leads.scene.filters.city_placeholder_no_region', {
                      region: regionLabel,
                    })
              }
              allowClear
              maxTagCount="responsive"
            />
          </Form.Item>
          <Divider plain style={{ margin: '16px 0 8px', fontSize: 12 }}>
            {t('scenes.leads.scene.filters.profile_divider')}
          </Divider>
          <Form.Item
            label={t('scenes.leads.scene.filters.nationality_label')}
            style={{ marginBottom: 12 }}
          >
            <Select
              mode="multiple"
              value={draftNationalities}
              onChange={(value) => setDraftNationalities(value as number[])}
              loading={nationalitiesLoading}
              disabled={nationalitiesLoading}
              options={nationalityOptions}
              optionFilterProp="label"
              showSearch
              style={{ width: '100%' }}
              placeholder={t(
                'scenes.leads.scene.filters.nationality_placeholder'
              )}
              allowClear
              maxTagCount="responsive"
            />
          </Form.Item>
          <Form.Item
            label={t('scenes.leads.scene.filters.gender_label')}
            style={{ marginBottom: 12 }}
          >
            <Select
              mode="multiple"
              value={draftGenders}
              onChange={(value) => setDraftGenders(value as number[])}
              loading={gendersLoading}
              disabled={gendersLoading}
              options={genderOptions}
              optionFilterProp="label"
              style={{ width: '100%' }}
              placeholder={t('scenes.leads.scene.filters.gender_placeholder')}
              allowClear
              maxTagCount="responsive"
            />
          </Form.Item>
          <Form.Item
            label={t('scenes.leads.scene.filters.marital_status_label')}
            style={{ marginBottom: 0 }}
          >
            <Select
              mode="multiple"
              value={draftMaritalStatuses}
              onChange={(value) => setDraftMaritalStatuses(value as number[])}
              loading={maritalStatusesLoading}
              disabled={maritalStatusesLoading}
              options={maritalStatusOptions}
              optionFilterProp="label"
              style={{ width: '100%' }}
              placeholder={t(
                'scenes.leads.scene.filters.marital_status_placeholder'
              )}
              allowClear
              maxTagCount="responsive"
            />
          </Form.Item>
          <div style={{ marginTop: 12 }}>
            <LeadTagsFilter
              country={draftCountry}
              active={filtersOpen}
              value={draftTags}
              match={draftTagMatch}
              onChange={setDraftTags}
              onMatchChange={setDraftTagMatch}
            />
          </div>
          <Form.Item
            label={t('scenes.leads.scene.filters.payment_label')}
            tooltip={t('scenes.leads.scene.filters.payment_tooltip')}
            style={{ marginTop: 12, marginBottom: 0 }}
          >
            <Select
              value={draftPayment}
              onChange={(value) => setDraftPayment(value as string | undefined)}
              options={[
                {
                  value: 'true',
                  label: t('scenes.leads.scene.filters.payment_configured'),
                },
                {
                  value: 'false',
                  label: t('scenes.leads.scene.filters.payment_pending'),
                },
              ]}
              style={{ width: '100%' }}
              placeholder={t('scenes.leads.scene.filters.payment_placeholder')}
              allowClear
            />
          </Form.Item>
        </Form>
      </div>
      <Divider style={{ margin: '16px 0 12px' }} />
      <Row justify="space-between" align="middle">
        <Col>
          <Button type="text" onClick={handleClearFilters}>
            {t('scenes.leads.scene.filters.clear_filters')}
          </Button>
        </Col>
        <Col>
          <Button type="primary" onClick={handleApplyFilters}>
            {t('scenes.leads.scene.filters.apply_filters')}
          </Button>
        </Col>
      </Row>
    </div>
  );

  return (
    <Layout title={''}>
      {contextHolder}

      <div className="leads-scene">
        <Card>
          <Row gutter={[16, 12]} align="middle" justify="space-between">
            <Col flex="none">
              <label className="filter-search-container">
                {t('scenes.leads.scene.filters.label')}{' '}
                <Search
                  placeholder={t(
                    'scenes.leads.scene.filters.search_placeholder'
                  )}
                  onSearch={onSearch}
                  disabled={tableLoading}
                  allowClear
                />
              </label>
            </Col>
            <Col flex="none">
              <Space size={8}>
                <Popover
                  trigger="click"
                  placement="bottomRight"
                  open={filtersOpen}
                  onOpenChange={handleFiltersOpenChange}
                  content={filtersContent}
                  destroyTooltipOnHide
                >
                  <Badge
                    count={activeFiltersCount}
                    size="small"
                    offset={[-4, 4]}
                  >
                    <Button icon={<FilterOutlined />}>
                      <Space size={6}>
                        {t('scenes.leads.scene.filters.advanced_filters')}
                      </Space>
                    </Button>
                  </Badge>
                </Popover>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => setIsExportModalOpen(true)}
                >
                  {t('scenes.leads.scene.export.button')}
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {bulk.count > 0 && (
          <div style={{ marginTop: '12px' }}>
            <BulkTagLeadsBar
              count={bulk.count}
              onTag={() => setIsBulkTagOpen(true)}
              onClear={() => bulk.setSelectedRowKeys([])}
            />
          </div>
        )}

        <Card style={{ marginTop: '12px' }}>
          <Table<LeadCard>
            dataSource={dataSource}
            bordered
            loading={tableLoading}
            size="small"
            scroll={{ x: 1200 }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
            }}
            rowKey={(record) => record.id}
            rowSelection={{
              selectedRowKeys: bulk.selectedRowKeys,
              onChange: (keys) => bulk.setSelectedRowKeys(keys),
            }}
            onChange={handleTableChange}
          >
            <Column
              title={t('scenes.leads.scene.table.columns.lead')}
              key="lead"
              sorter
              sortOrder={getSortOrder('fullName')}
              render={(row: LeadCard) => (
                <div>
                  <div className="lead-cell-primary">
                    <button
                      type="button"
                      className="lead-name-link"
                      onClick={() => showDetailLead(row)}
                    >
                      {row.firstName} {row.lastName}
                    </button>
                  </div>
                  <div className="lead-cell-secondary">{row.email}</div>
                </div>
              )}
            />

            <Column
              title={t('scenes.leads.scene.table.columns.location')}
              key="location"
              render={(row: LeadCard) => renderLocation(row)}
            />

            <Column
              title={t('scenes.leads.scene.table.columns.university_career')}
              key="universityCareer"
              render={(row: LeadCard) => (
                <div>
                  <div className="lead-cell-primary">
                    {row.universityName ||
                      row.otherUniversity ||
                      t('scenes.leads.scene.table.empty_universidad')}
                  </div>
                  <div className="lead-cell-secondary">
                    {row.career || t('scenes.leads.scene.table.empty_career')}
                  </div>
                </div>
              )}
            />

            <Column
              width={220}
              title={t('scenes.leads.scene.table.columns.tags')}
              key="tags"
              render={(row: LeadCard) => <LeadTagsCell tags={row.tags} />}
            />

            <Column
              width={200}
              align="left"
              title={t('scenes.leads.scene.table.columns.status')}
              key="status"
              sorter
              sortOrder={getSortOrder('statusCode')}
              render={(row: LeadCard) =>
                renderStatus(
                  row.statusCode,
                  i18n.language === 'en' ? row.statusNameEn : row.statusNameEs
                )
              }
            />

            <Column
              width={170}
              align="center"
              title={t('scenes.leads.scene.table.columns.created_at')}
              key="createdAt"
              sorter
              sortOrder={getSortOrder('createdAt')}
              render={(row: LeadCard) => (
                <span>{formatDateCell(row.createdAt)}</span>
              )}
            />

            <Column
              width={70}
              align="center"
              title=""
              key="actions"
              render={(row: LeadCard) => (
                <Dropdown
                  menu={{ items: getDropdownMenu(row) }}
                  placement="bottomLeft"
                  trigger={['click']}
                >
                  <Button size="small">
                    <MoreOutlined />
                  </Button>
                </Dropdown>
              )}
            />
          </Table>
        </Card>
      </div>

      {selectedLeadId && (
        <DetailLeadModal
          isOpen={isDetailModalOpen}
          leadId={selectedLeadId}
          handleModalCancel={handleCloseDetailModal}
          onAfterUpdate={handleAfterUpdate}
          statuses={statuses}
          navigation={{
            onPrev: handleNavigatePrev,
            onNext: handleNavigateNext,
            canPrev: canNavigatePrev,
            canNext: canNavigateNext,
            loading: pendingNavSelect !== null,
            position:
              currentLeadIndex >= 0
                ? { current: currentGlobalIndex + 1, total }
                : null,
            // Solo mostramos los mensajes de fuera-de-filtro cuando el lead
            // realmente dejo de coincidir; en un simple reorden (fantasma sin
            // outOfFilter) las flechas siguen activas pero sin avisos.
            outOfFilter: isGhostMode && !!ghost?.outOfFilter,
          }}
        />
      )}

      <BulkTagLeadsModal
        open={isBulkTagOpen}
        {...bulk.modalProps}
        onClose={() => setIsBulkTagOpen(false)}
      />

      <ExportLeadsModal
        isOpen={isExportModalOpen}
        country={country}
        statuses={statuses}
        handleModalCancel={() => setIsExportModalOpen(false)}
      />
    </Layout>
  );
}

export default Leads;
